import React, { useState } from 'react';
import { Col } from "react-bootstrap";
import "../assets/styles/style.css";
import { getLanguageColor, formatRelativeTime } from '../utils/githubService';

export const ProjectCard = ({
  title,
  description,
  tech,
  onClick,
  stars,
  forks,
  language,
  lastUpdated,
  githubUrl,
  homepage,
  isLive
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const getGithubLink = (desc) => {
    const match = desc.match(/href="(https:\/\/github\.com\/[^"]+)"/);
    return match ? match[1] : null;
  };

  const getLiveLink = (desc) => {
    const match = desc.match(/href="(https:\/\/(?!github\.com)[^"]+)"/);
    return match ? match[1] : null;
  };

  const githubLink = getGithubLink(description);
  const liveLink = getLiveLink(description);

  return (
    <Col size={12} sm={6} md={4}>
      <div
        style={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px',
          padding: '30px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: isHovered
            ? '0 20px 60px rgba(170, 54, 124, 0.4), 0 0 40px rgba(74, 47, 189, 0.3)'
            : '0 8px 32px rgba(0, 0, 0, 0.3)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: isHovered ? 'translateY(-10px) scale(1.02)' : 'translateY(0) scale(1)',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '420px', // Minimum height, allows expansion
          height: isExpanded ? 'auto' : '420px' // Dynamic height based on expansion
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={onClick}
      >
        {/* Gradient Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90.21deg, #AA367C -5.91%, #4A2FBD 111.58%)',
          opacity: isHovered ? 1 : 0.5,
          transition: 'opacity 0.3s ease'
        }} />

        {/* Title */}
        <h4 style={{
          color: '#fff',
          fontSize: '22px',
          fontWeight: '700',
          marginBottom: '15px',
          marginTop: '10px',
          lineHeight: '1.3'
        }}>
          {title}
        </h4>

        {/* Description */}
        <div style={{ marginBottom: '20px' }}>
          <p style={{
            color: '#B8B8B8',
            fontSize: '14px',
            lineHeight: '1.6',
            marginBottom: description.replace(/<[^>]+>/g, '').length > 150 ? '10px' : '0'
          }}>
            {(() => {
              const cleanDesc = description.replace(/<[^>]+>/g, '');
              if (cleanDesc.length <= 150) return cleanDesc;
              if (isExpanded) return cleanDesc;
              return cleanDesc.substring(0, 150) + '...';
            })()}
          </p>
          {description.replace(/<[^>]+>/g, '').length > 150 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#AA367C',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                padding: '0',
                textDecoration: 'underline',
                transition: 'color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.color = '#4A2FBD'}
              onMouseLeave={(e) => e.target.style.color = '#AA367C'}
            >
              {isExpanded ? 'See Less' : 'See More'}
            </button>
          )}
        </div>

        {/* GitHub Stats - only show for live GitHub repos */}
        {isLive && (stars !== undefined || forks !== undefined || language) && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            marginBottom: '15px',
            paddingBottom: '15px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            {stars !== undefined && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                color: '#fff',
                fontSize: '13px',
                fontWeight: '600'
              }}>
                <span style={{ fontSize: '16px' }}>⭐</span>
                <span>{stars}</span>
              </div>
            )}
            {forks !== undefined && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                color: '#fff',
                fontSize: '13px',
                fontWeight: '600'
              }}>
                <span style={{ fontSize: '16px' }}>🍴</span>
                <span>{forks}</span>
              </div>
            )}
            {language && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'rgba(255, 255, 255, 0.1)',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                <span style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: getLanguageColor(language)
                }} />
                <span style={{ color: '#fff' }}>{language}</span>
              </div>
            )}
            {lastUpdated && (
              <div style={{
                marginLeft: 'auto',
                color: '#888',
                fontSize: '12px',
                fontStyle: 'italic'
              }}>
                Updated {formatRelativeTime(lastUpdated)}
              </div>
            )}
          </div>
        )}

        {/* Technology Tags */}
        {tech && tech.length > 0 && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            marginBottom: '20px'
          }}>
            {tech.map((technology, idx) => (
              <span key={idx} style={{
                background: 'linear-gradient(90.21deg, rgba(170, 54, 124, 0.3) -5.91%, rgba(74, 47, 189, 0.3) 111.58%)',
                border: '1px solid rgba(170, 54, 124, 0.5)',
                color: '#fff',
                padding: '6px 14px',
                borderRadius: '15px',
                fontSize: '12px',
                fontWeight: '600',
                backdropFilter: 'blur(5px)'
              }}>
                {technology}
              </span>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginTop: 'auto', // Push buttons to bottom
          paddingTop: '15px' // Add some spacing
        }}>
          {(githubLink || githubUrl) && (
            <a
              href={githubUrl || githubLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                flex: 1,
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#fff',
                padding: '12px 20px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '600',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(5px)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 15px rgba(255, 255, 255, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <span style={{ fontSize: '16px' }}>📂</span>
              <span>Code</span>
            </a>
          )}
          {(liveLink || homepage) && (
            <a
              href={homepage || liveLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                flex: 1,
                background: 'linear-gradient(90.21deg, #AA367C -5.91%, #4A2FBD 111.58%)',
                border: 'none',
                color: '#fff',
                padding: '12px 20px',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '600',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(170, 54, 124, 0.4)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 25px rgba(170, 54, 124, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(170, 54, 124, 0.4)';
              }}
            >
              <span style={{ fontSize: '16px' }}>🔗</span>
              <span>Live Demo</span>
            </a>
          )}
        </div>
      </div>
    </Col>
  );
};